// src/pages/admin/AdminDashboard.jsx

import { useNavigate, useSearchParams } from "react-router-dom";
import React from "react";
import "../../styles/adminDashboard.css";

import { useAdminScope } from "../../context/AdminScopeContext";
import { useCompany } from "../../context/CompanyContext";
import AdminActivityScopeSelector from "./AdminActivityScopeSelector";
import {
  ADMIN_PANEL_REGISTRY,
  getAdminPanelConfig,
  isRegisteredAdminPanel,
} from "./panel_engine";
import {
  ADMIN_ACTION_REGISTRY,
  AdminActionGrid,
} from "./navigation_engine";
import { assertAdminRegistryIntegrity } from "./registry_engine";
import {
  AdminPanelRuntime,
  AdminRuntimeProvider,
} from "./runtime_engine";
import { useWorkspaceEngine } from "./workspace_engine";

export const ADMIN_REGISTRY_VALIDATION =
  assertAdminRegistryIntegrity({
    navigationRegistry: ADMIN_ACTION_REGISTRY,
    panelRegistry: ADMIN_PANEL_REGISTRY,
  });

const ADMIN_TABS = [
  { key: "users", label: "Utilisateurs/Clients" },
  {
    key: "hr",
    label: "Ressources humaines",
    subtitle: "Référentiel Employee et administration RH",
  },
  {
    key: "cash-management",
    label: "Gestion Caisse",
    subtitle: "Activités, caisses, menus, articles et catalogues",
  },
  { key: "loyalty", label: "Fidélité" },
  {
    key: "studio",
    label: "Studio",
    subtitle: "Conception des espaces, salles et interfaces",
  },
  {
    key: "suppliers",
    label: "Fournisseurs",
    subtitle: "Fournisseurs, approvisionnements et réceptions",
  },
  { key: "exports", label: "Exports" },
  { key: "banking", label: "Banque" },
  { key: "cert", label: "Certificats" },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    selectedCompanyUuid,
    selectedCompanyName,
    resetScope,
    isGlobalScope,
  } = useAdminScope();

  const { isSuperAdmin } = useCompany();

  const workspace = useWorkspaceEngine({
    searchParams,
    setSearchParams,
    getPanelConfig: getAdminPanelConfig,
  });

  const {
    activeTab,
    panelMode,
    panelContext,
    panelKey,
    isFullscreen,
    isPanelExpanded,
    changeTab,
    openPanel,
    closePanel,
    toggleFullscreen,
  } = workspace;

  const hasRegisteredPanel = isRegisteredAdminPanel(panelMode);

  return (
    <AdminRuntimeProvider
      workspace={workspace}
      getPanelConfig={getAdminPanelConfig}
      isRegisteredPanel={isRegisteredAdminPanel}
    >
      <div className="admin-dashboard-page">
      <div className="admin-dashboard-header">
        <h1 className="admin-dashboard-title">Dashboard Administrateur</h1>

        {isSuperAdmin && (
          <div className="admin-dashboard-scope-bar">
            <div>
              <strong>Scope actif :</strong>{" "}
              {isGlobalScope
                ? "Global"
                : selectedCompanyName || selectedCompanyUuid || "Société courante"}
            </div>

            <AdminActivityScopeSelector />

            <button type="button" onClick={resetScope}>
              Scope global
            </button>
          </div>
        )}

        <div className="admin-dashboard-tabs">
          {ADMIN_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`admin-dashboard-tab ${
                activeTab === tab.key ? "active_tab" : ""
              }`}
              onClick={() => changeTab(tab.key)}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-dashboard-content">
        <main
          className={`admin-dashboard-workspace ${
            isPanelExpanded ? "is-expanded" : ""
          }`}
        >
          <section
            className={`admin-dashboard-main ${
              isPanelExpanded ? "is-compact" : ""
            }`}
          >
            <div
              className={`admin-dashboard-body ${
                isPanelExpanded ? "is-compact" : ""
              }`}
            >
              <AdminActionGrid
                activeTab={activeTab}
                density={isPanelExpanded ? "mini" : "large"}
                navigate={navigate}
                onOpenPanel={openPanel}
              />
            </div>
          </section>

          <aside
            className={`admin-dashboard-side-panel ${
              isPanelExpanded ? "is-expanded" : ""
            } ${isFullscreen ? "is-fullscreen" : ""}`}
          >
            <div className="admin-dashboard-report-container">
              <AdminPanelRuntime
                fallback={
                  <div className="admin-dashboard-info-panel">
                    <h3>
                      {ADMIN_TABS.find(
                        (tab) => tab.key === activeTab
                      )?.label}
                    </h3>
                    <p>
                      Sélectionne une action dans le panneau principal pour
                      accéder au module métier correspondant.
                    </p>
                  </div>
                }
              />
            </div>
          </aside>
        </main>
      </div>
      </div>
    </AdminRuntimeProvider>
  );
};

export default AdminDashboard;
